import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { readHeaders } from './fileHeaders';
import { HelpPopoverButton } from './HelpPopoverButton';
import { ColumnHelp } from './constants';

export type FileSlotState = {
    file: File | null;
    columns: string[];
    allColumns: string[];
};

type FileSlotProps = {
    label: string;
    required: boolean;
    onChange: (state: FileSlotState) => void;
    onRemove?: () => void;
    disabled?: boolean;
    invalid?: boolean;
    hideColumns?: boolean;
    file?: File | null;
    helpColumns?: ColumnHelp[];
};

export const FileSlot = ({
    label,
    required,
    onChange,
    onRemove,
    disabled = false,
    invalid = false,
    hideColumns = false,
    file: fileProp,
    helpColumns = [],
}: FileSlotProps) => {
    const [internalFile, setInternalFile] = useState<File | null>(null);
    const file = fileProp !== undefined ? fileProp : internalFile;
    const [headers, setHeaders] = useState<string[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const [parsing, setParsing] = useState(false);
    // Header parsing is async and a slot can be hidden mid-parse (a clustering toggle
    // switched off, for instance), which unmounts this component while the promise is
    // still pending. Track mounted state so the callback below doesn't write to a
    // component that is gone.
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files?.[0];
        e.target.value = '';
        if (!picked) return;

        setParsing(true);
        setError(null);
        try {
            const parsedHeaders = await readHeaders(picked);
            if (!mounted.current) return;
            setInternalFile(picked);
            setHeaders(parsedHeaders);
            setSelected(new Set());
            onChange({ file: picked, columns: [], allColumns: parsedHeaders });
        } catch (err: any) {
            if (!mounted.current) return;
            setInternalFile(null);
            setHeaders([]);
            setSelected(new Set());
            setError(err?.message ?? 'Failed to parse file.');
            onChange({ file: null, columns: [], allColumns: [] });
        } finally {
            if (mounted.current) setParsing(false);
        }
    };

    // The page only clears its own state on remove; the parsed headers, selection
    // and any parse error live here and must not survive into the emptied slot.
    const handleRemove = () => {
        setInternalFile(null);
        setHeaders([]);
        setSelected(new Set());
        setError(null);
        onRemove?.();
    };

    const toggleColumn = (name: string) => {
        const next = new Set(selected);
        if (next.has(name)) next.delete(name);
        else next.add(name);
        setSelected(next);
        onChange({ file, columns: Array.from(next), allColumns: headers });
    };

    const borderColor = invalid ? 'error.main' : 'rgba(0,0,0,0.12)';

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor,
                borderRadius: 2,
                padding: 2,
                backgroundColor: disabled ? '#f0f0f0' : '#fafbff',
                opacity: disabled ? 0.6 : 1,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{label}</Typography>
                <Chip
                    size="small"
                    label={required ? 'Required' : 'Optional'}
                    color={required ? 'primary' : 'default'}
                    variant="outlined"
                />
                <HelpPopoverButton
                    ariaLabel={`${label} columns help`}
                    renderContent={pinned => (
                        helpColumns.length === 0 ? (
                            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                                No column details yet.
                            </Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                {helpColumns.map(c => (
                                    <Typography key={c.name} variant="body2">
                                        <strong>{c.name}</strong>
                                        {pinned && <> — <span>{c.description}</span></>}
                                    </Typography>
                                ))}
                            </Stack>
                        )
                    )}
                />
                <Box sx={{ flex: 1 }} />
                {onRemove && (
                    // Disabled mid-parse: removing while a replacement is being read
                    // would let the pending parse re-fill the slot just emptied.
                    <IconButton size="small" onClick={handleRemove} aria-label="Remove file" disabled={disabled || parsing}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
                <Button variant="outlined" component="label" disabled={parsing || disabled}>
                    {file ? 'Replace file' : 'Choose file'}
                    <input
                        type="file"
                        hidden
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFile}
                        disabled={disabled}
                    />
                </Button>
                {parsing && <CircularProgress size={20} />}
                {!parsing && file && (
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)' }}>
                        {file.name}
                    </Typography>
                )}
                {!parsing && !file && !error && (
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.5)' }}>
                        No file selected
                    </Typography>
                )}
            </Stack>

            {invalid && !file && (
                <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>
                    File required
                </Typography>
            )}

            {error && (
                <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>
                    {error}
                </Typography>
            )}

            {!hideColumns && headers.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', mb: 1 }}>
                        Select columns to include:
                    </Typography>
                    <FormGroup row>
                        {headers.map(h => (
                            <FormControlLabel
                                key={h}
                                control={
                                    <Checkbox
                                        checked={selected.has(h)}
                                        onChange={() => toggleColumn(h)}
                                    />
                                }
                                label={h}
                            />
                        ))}
                    </FormGroup>
                </Box>
            )}
        </Box>
    );
};
